// Not `server-only`: these run in both runtimes so one `queryOptions` entry serves the
// server prefetch and a client refetch. The handshake is always passed in, never read from
// module scope, so no credential reaches the browser bundle from here.
import { ApiError } from "~/api/api-error";
import { ENV_API_ORIGIN } from "~/app/environment/server";
import type {
  ActivityKeyword,
  BookingActivityCategory,
  IEnvWeb,
} from "~/types/api-types";

// Next's Data Cache — the only cache shared across requests. TanStack's is per-request on
// the server and per-tab in the browser.
const ENVIRONMENT_REVALIDATE_SECONDS = 3600;

const DEFAULT_LOCALE = "en-US";

/** The env service wraps the keyword list in an envelope rather than returning a bare array. */
type ActivityKeywordsResponse = {
  response?: ActivityKeyword[];
};

const isServer = (): boolean => typeof window === "undefined";

// The two runtimes have always encoded this differently and upstream accepts both. They
// agree for any URL-safe handshake; preserved rather than unified on a guess.
const toHandshakeHeader = (handshake: string): string =>
  isServer()
    ? Buffer.from(handshake).toString("base64")
    : btoa(encodeURIComponent(handshake));

/** Calls the env service direct on the server, through the app's `/v1` proxy in the browser. */
const fetchFromEnvService = async ({
  browserPath,
  handshake,
  serverPath,
}: {
  browserPath: string;
  handshake: string;
  serverPath: string;
}): Promise<Response> => {
  const headers = { Authorization: `Bearer ${toHandshakeHeader(handshake)}` };

  if (isServer()) {
    return fetch(`${ENV_API_ORIGIN}${serverPath}`, {
      headers,
      next: { revalidate: ENVIRONMENT_REVALIDATE_SECONDS },
    });
  }

  return fetch(browserPath, { headers });
};

/**
 * Activity keywords for a locale — the options behind activity autocompletes used for searches.
 * Rejects on failure so ISR keeps serving the last good page and subscribers see `isError`.
 */
export const fetchActivityKeywords = async ({
  handshake,
  locale,
}: {
  handshake: string;
  locale: string;
}): Promise<ActivityKeyword[]> => {
  const resolvedLocale = locale || DEFAULT_LOCALE;
  const response = await fetchFromEnvService({
    browserPath: `/v1/env/keywords/${resolvedLocale}`,
    handshake,
    serverPath: `/env/keywords/${resolvedLocale}`,
  });

  if (!response.ok) {
    throw new ApiError(
      `Failed to fetch activity keywords for locale "${resolvedLocale}"`,
      response.status,
    );
  }

  const payload = (await response.json()) as ActivityKeywordsResponse;

  // A 200 with the wrong shape is a contract violation; passing `undefined` down would
  // crash a `.map` two frames later.
  if (!Array.isArray(payload.response)) {
    throw new Error(
      `Malformed activity keywords payload for locale "${resolvedLocale}": expected a "response" array`,
    );
  }

  return payload.response;
};

/**
 * Returns the env service's booking activities grouped by use-type category
 * (e.g. "Meeting", "Events", "Productions") for a locale. These are the options used for bookings.
 */
export const fetchActivityCategories = async ({
  handshake,
  locale,
}: {
  handshake: string;
  locale: string;
}): Promise<BookingActivityCategory[]> => {
  const resolvedLocale = locale || DEFAULT_LOCALE;
  const response = await fetchFromEnvService({
    browserPath: `/v1/env/activity-categories/${resolvedLocale}`,
    handshake,
    serverPath: `/env/activity-categories/${resolvedLocale}`,
  });

  if (!response.ok) {
    throw new ApiError(
      `Failed to fetch activity categories for locale "${resolvedLocale}"`,
      response.status,
    );
  }

  return (await response.json()) as BookingActivityCategory[];
};

/**
 * Feature flags and the hero's location prefill. The service ignores the locale segment,
 * but each runtime keeps the route it has always called rather than changing on that.
 *
 * `Partial`, not `IEnvWeb`: the payload is config-authored, so any field can be absent
 * while `IEnvWeb` declares every one of them required. Callers narrow what they read.
 */
export const fetchEnvWeb = async ({
  handshake,
  locale,
}: {
  handshake: string;
  locale: string;
}): Promise<Partial<IEnvWeb>> => {
  const response = await fetchFromEnvService({
    browserPath: `/v1/env/web/${locale || DEFAULT_LOCALE}`,
    handshake,
    serverPath: "/env/web",
  });

  if (!response.ok) {
    throw new ApiError("Failed to fetch web environment data", response.status);
  }

  return (await response.json()) as Partial<IEnvWeb>;
};
